"use client";

import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import Link from "next/link";

import {
  IBlog,
  ICallToAction,
  ICallToActionFields,
  INavItem,
  INavItemFields,
  INavLinkFields,
  INavSectionFields,
  IPageFields,
} from "@/types/generated/contentful";
import CallToAction from "../callToAction";
import { BlogPostTile } from "../blogLayout/blogPostTitle";

type Props = {
  item: INavItem | ICallToAction;
  closeNav: Function;
  expanded: boolean;
  itemSelected: Function;
};

const NavItem: FunctionComponent<Props> = ({
  item,
  expanded,
  closeNav,
  itemSelected,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isNavItem = (item.fields as INavItemFields).sections ? true : false;

  useEffect(() => {
    setIsOpen(expanded);
  }, [expanded]);

  if (isNavItem) {
    const content = item.fields as INavItemFields;
    return (
      <li
        className={`z-50 h-16 p-5 text-base ${
          isOpen ? "bg-light-primary" : "bg-white"
        }`}
      >
        <button
          type="button"
          className="flex items-center gap-x-1"
          aria-expanded={isOpen}
          onBlur={() => {
            setIsOpen(false);
          }}
          onClick={() => {
            !isOpen && itemSelected();
            setIsOpen(!isOpen);
          }}
        >
          {content.title}
          {content.sections.length && (
            <div
              className={`transition-all duration-500 ease-in-out ${isOpen ? "-rotate-180" : ""}`}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12.2836 6.84729C12.9996 5.97796 12.3802 4.66663 11.2536 4.66663H4.7469C3.62023 4.66663 3.00157 5.97796 3.71757 6.84729L6.97157 10.7986C7.09665 10.9506 7.25382 11.0729 7.43179 11.1569C7.60975 11.2409 7.80411 11.2845 8.0009 11.2845C8.19769 11.2845 8.39205 11.2409 8.57001 11.1569C8.74798 11.0729 8.90515 10.9506 9.03023 10.7986L12.2836 6.84729Z"
                  fill="currentColor"
                />
              </svg>
            </div>
          )}
        </button>
        {/* Mega Nav Drawer */}
        <div
          className={`absolute left-0 top-full w-full p-4 ${isOpen ? "translate-y-0 border-t-2 border-light-primary opacity-100 shadow-md" : "pointer-events-none -translate-y-2 opacity-0"} duration-400 z-100 bg-white transition-all`}
        >
          <div className="m-auto flex justify-center py-6 xl:container">
            {content &&
              content.sections.map((section, index) => {
                const sectionFields = section.fields as INavSectionFields;
                const isBlog = (section.fields as INavSectionFields).items
                  ? false
                  : true;

                if (isBlog) {
                  return (
                    <div
                      key={index}
                      className="max-w-lg"
                      tabIndex={-1}
                      onBlur={() => {
                        if (index === content.sections.length - 1) {
                          setIsOpen(false);
                        }
                      }}
                      onFocus={() => {
                        setIsOpen(true);
                        itemSelected();
                      }}
                    >
                      <BlogPostTile
                        content={section as IBlog}
                        nav={true}
                        tabIndex={isOpen ? 0 : -1}
                      />
                    </div>
                  );
                }
                return (
                  <div key={index} className="flex max-w-lg flex-col gap-2">
                    <span className="text-lg font-bold text-primary">
                      {sectionFields.title}
                    </span>
                    <ul className="flex flex-col">
                      {sectionFields.items.map((item, index) => {
                        const itemFields = item.fields as INavLinkFields;
                        const isPage = (itemFields.page.fields as IPageFields)
                          .content
                          ? true
                          : false;
                        const pageFields = itemFields.page
                          .fields as IPageFields;

                        return (
                          <li
                            key={index}
                            className="pointer -translate-x-4 rounded-md p-4 text-base hover:bg-gray-100"
                          >
                            <Link
                              href={
                                isPage
                                  ? `/${pageFields.slug}`
                                  : `/insights?tag=${pageFields.slug}`
                              }
                              className=""
                              tabIndex={isOpen ? 0 : -1}
                              onBlur={() => {
                                if (
                                  index == sectionFields.items.length - 1 ||
                                  index == 0
                                ) {
                                  setIsOpen(false);
                                }
                              }}
                              onFocus={() => {
                                setIsOpen(true);
                                itemSelected();
                              }}
                              onClick={() => setIsOpen(false)}
                            >
                              <div className="flex flex-col">
                                <span className="font-bold">
                                  {itemFields.title}
                                </span>
                                {itemFields.description}
                              </div>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
          </div>
        </div>
      </li>
    );
  }

  return (
    <li>
      <CallToAction
        content={item as ICallToAction}
        nav={true}
        appendClass={`${(item.fields as ICallToActionFields).style === "tertiary" ? "!text-base" : ""}`}
      />
    </li>
  );
};

export default NavItem;
